import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateDb, formatDateHourDb } from "../utils/tools";
import { customDateFormatType, formatDateForInput } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.makeupWorkRequest;
const queryKey = "makeupWorkRequest";

async function fetchMakeupWorkRequest(
  currentPage,
  creatorId,
  status,
  startTime,
  endTime
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (creatorId) options.employeeId = creatorId;
    if (status) options.status = status;

    if (startTime) options.startTime = customDateFormatType(startTime);
    if (endTime) options.endTime = customDateFormatType(endTime);

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/makeupWorkRequest?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        status: item?.status || "-",
        codeEmployee: item?.employeeObject?.code || "",
        nameEmployee: item?.employeeObject?.fullName || "-",
        images: item?.images || [],
        dayLeave: item?.dayLeave ? formatDateDb(item?.dayLeave) : "",
        note: item?.note || "-",
        nameEmployeeConfirm: item?.employeeConfirmObject?.fullName || "-",
        createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "-",
        edit: {
          id: item?._id || "",
          employeeId: item?.employeeId || "",
          images: item?.images || [],
          dayLeave: item?.dayLeave ? formatDateForInput(item?.dayLeave) : "",
          note: item?.note || "",
          status: item?.status || "",
          workingDayRate: item?.workingDayRate || "",
        },
        detail: {
          id: item?._id || "",
          status: item?.status || "-",
          codeEmployee: item?.employeeObject?.code || "",
          nameEmployee: item?.employeeObject?.fullName || "-",
          employeeId: item?.employeeId || "",
          images: item?.images || [],
          dayLeave: item?.dayLeave ? formatDateDb(item?.dayLeave) : "",
          note: item?.note || "-",
          nameEmployeeConfirm: item?.employeeConfirmObject?.fullName || "",
          employeeConfirmId: item?.employeeConfirmId || "",
          approverDate: item?.approverDate
            ? formatDateHourDb(item?.approverDate)
            : "",
          workingDayRate: item?.workingDayRate || "",
          createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "",
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useMakeupWorkRequest(creatorId, status, startDate, endDate) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [queryKey, currentPage, creatorId, status, startDate, endDate],
    queryFn: () =>
      fetchMakeupWorkRequest(
        currentPage,
        creatorId,
        status,
        startDate,
        endDate
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    makeupWorkRequestData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
